import { Component, Event, EventEmitter, JSX, Method, Prop, Watch, h } from '@stencil/core'
import { Datepicker } from 'vanillajs-datepicker'

import { DatepickerContext, DatepickerStrategy } from './air-datepicker.strategy'
import { DatepickerMode, DatepickerOptions } from './air-datepicker.types'
import { KeyCode } from '../../enums/keycode.enum'
import { Validation } from '../../types/status.types'
import { isUndefined } from '../../utils/helpers'

@Component({
  tag: 'air-datepicker',
  styleUrl: 'air-datepicker.scss',
  shadow: true,
})
export class AirDatepicker {
  /** Defines name */
  @Prop({ reflect: true }) name?: string

  /** Specifies datepicker label */
  @Prop() label?: string

  /** Specifies placeholder value */
  @Prop({ mutable: true }) placeholder?: string

  /** Specifies if datepicker is read only */
  @Prop({ reflect: true }) readonly = false

  /** Specifies if datepicker is required */
  @Prop({ reflect: true }) required = false

  /** Specifies if datepicker is disabled */
  @Prop({ reflect: true }) disabled = false

  /** Specifies validation of datepicker */
  @Prop() validation?: Validation

  /** Defines icon on the left side */
  @Prop() icon = 'calendar'

  /** Defines the icon clear text */
  @Prop() iconClear = 'x'

  /** Datepicker preview arrow icon name */
  @Prop() iconDatepickerPrevArrow = 'chevron-left'

  /** Datepicker next arrow icon name */
  @Prop() iconDatepickerNextArrow = 'chevron-right'

  /** Specifies if datepicker should have autofocus */
  // eslint-disable-next-line @stencil-community/reserved-member-names
  @Prop() autofocus = false

  /** Specifies mode of datepicker picker */
  @Prop() mode: DatepickerMode = 'date'

  /** Defines the open state */
  @Prop({ mutable: true, reflect: true }) opened = false

  /** Specifies if datepicker opens on focus */
  @Prop() quickPick = false

  /** Defines datepicker value */
  @Prop({ mutable: true }) value?: string
  @Watch('value')
  valueChanged(newValue: string, oldValue: string): void {
    if (newValue !== oldValue) {
      this.setDatepickerValue(newValue)
    }
  }

  /** Specifies minimum limit to selectable date */
  @Prop() minDate?: string | number | Date

  /** Specifies maximum limit to selectable date */
  @Prop() maxDate?: string | number | Date

  /** Triggers when input value was changed and blur */
  @Event({ eventName: 'air-change' }) change!: EventEmitter<string>

  /** Triggers when input in focus */
  @Event({ eventName: 'air-focus' }) focus!: EventEmitter<string>

  /** Triggers when an input has lost focus */
  @Event({ eventName: 'air-blur' }) blur!: EventEmitter<string>

  private datepicker!: Datepicker
  private strategy!: DatepickerStrategy
  private datepickerElement!: HTMLDivElement
  private airInputElement!: HTMLAirInputElement

  /** Manually sets focus to the input element */
  @Method()
  async setFocus(): Promise<void> {
    await this.airInputElement?.setFocus()
  }

  componentWillLoad(): void {
    this.strategy = new DatepickerContext(this).getStrategy()
    this.placeholder = this.getPlaceholder()
  }

  async componentDidLoad(): Promise<void> {
    this.autofocus && (await this.setFocus())
    const datepickerOptions: DatepickerOptions = {
      minDate: this.minDate,
      maxDate: this.maxDate,
      ...this.strategy.getDatepickerOptions(),
    }
    this.datepicker = new Datepicker(this.datepickerElement, {
      prevArrow: `<air-icon name="${this.iconDatepickerPrevArrow}"></air-icon>`,
      nextArrow: `<air-icon name="${this.iconDatepickerNextArrow}"></air-icon>`,
      ...datepickerOptions,
    })

    this.datepickerElement.addEventListener('changeDate', this.handleChangeDate)
    this.datepickerElement.addEventListener('changeView', this.handleViewChange)

    this.setDaysFocusable()
  }

  disconnectedCallback(): void {
    this.datepicker?.destroy()
    this.datepickerElement.removeEventListener('changeDate', this.handleChangeDate)
    this.datepickerElement.removeEventListener('changeView', this.handleViewChange)
  }

  private getPlaceholder(): string {
    if (!isUndefined(this.placeholder)) return this.placeholder

    return this.strategy.getPlaceholder()
  }

  private setDatepickerValue(value: string): void {
    this.value = value
    this.updateDatepicker(value)
  }

  private updateDatepicker(value: string): void {
    if (value === undefined) {
      this.value = ''
      this.datepicker.setDate({ clear: true })
      return
    }
    const date = this.strategy.getDate(value)
    this.datepicker.setDate(date)
  }

  // TODO: Update CustomEvent type after vanillajs-datepicker team releases types support
  private handleChangeDate = (event: CustomEvent<{ date: Date }>): void => {
    this.value = this.strategy.getValue(event.detail.date)
    this.opened = false
    this.change.emit(this.value)
  }

  private handleViewChange = (): void => {
    setTimeout(() => {
      this.setDaysFocusable()
    })
  }

  private handleAirInputFocus = (event: FocusEvent): void => {
    event.stopPropagation()
    if (this.disabled || this.readonly || !this.quickPick) return
    this.opened = true
  }

  private handleAirInputIconClick = (event: CustomEvent<MouseEvent>): void => {
    event.stopPropagation()
    if (this.disabled || this.readonly || this.quickPick) return
    this.opened = !this.opened
  }

  private setDaysFocusable(): void {
    this.datepickerElement.querySelectorAll('.datepicker-cell:not(.disabled)').forEach((cell) => {
      cell.setAttribute('tabindex', '0')
    })
  }

  private handleAirInputIconKeyDown = (event: CustomEvent<KeyboardEvent>): void => {
    event.stopPropagation()
    if (this.disabled || this.readonly) return
    const keyCodes: string[] = [KeyCode.Space, KeyCode.Enter]
    if (keyCodes.includes(event.detail.code)) {
      this.opened = !this.opened
    }
  }

  private handleAirInputChange = (event: CustomEvent<string>): void => {
    this.updateDatepicker(event.detail)
  }

  private handleDropdownClose = (): void => {
    this.opened = false
  }

  private handleInputClick = (event: MouseEvent): void => {
    event.stopPropagation()
  }

  private handleDatepickerKeyDown = (event: KeyboardEvent): void => {
    const keyCodes: string[] = [KeyCode.Space, KeyCode.Enter]
    if (
      keyCodes.includes(event.code) &&
      event.target instanceof HTMLElement &&
      event.target.classList.contains('datepicker-cell')
    ) {
      event.target.click()
    }
  }

  render(): JSX.Element {
    const inputAttributes = {
      slot: 'trigger',
      type: this.strategy.getType(),
      class: 'datepicker-input',
      value: this.value,
      label: this.label,
      placeholder: this.placeholder,
      readonly: this.readonly,
      required: this.required,
      disabled: this.disabled,
      validation: this.validation,
      icon: this.icon,
      'icon-focusable': !this.disabled && !this.readonly && !this.quickPick,
      'icon-clear': this.iconClear,
      ref: (el: HTMLAirInputElement | undefined): void => {
        if (el) this.airInputElement = el
      },
      onClick: this.handleInputClick,
      onFocus: this.handleAirInputFocus,
      'onAir-change': this.handleAirInputChange,
      'onAir-icon-click': this.handleAirInputIconClick,
      'onAir-icon-keydown': this.handleAirInputIconKeyDown,
    }
    return (
      <div class="datepicker">
        <air-dropdown-base opened={this.opened} onAir-close={this.handleDropdownClose}>
          <air-input {...inputAttributes}></air-input>
          <div
            class="datepicker-wrapper"
            ref={(el: HTMLDivElement | undefined): void => {
              if (el) this.datepickerElement = el
            }}
            onKeyDown={this.handleDatepickerKeyDown}
          ></div>
        </air-dropdown-base>
      </div>
    )
  }
}
